import { Modal } from 'react-bootstrap';
import styled, { css } from 'styled-components';

// Four independent designs (all mounted; CSS toggles which one is visible so
// there's no layout flash on resize). The viewport's aspect ratio picks the
// design whose canvas shape is closest to it:
//
//   desktop 1920x1080 (1.78) | laptop 1225x1018 (1.20) | tablet 1024x1366 (0.75)
//   | mobile 1080x1740 (0.62)
//
// Each threshold is roughly the geometric mean of the two neighbouring canvas
// ratios. The mobile canvas is the 1080x1920 design with the horizontal
// sponsor strip cut off: on mobile the sponsors are a vertical list rendered
// below the key visual (MobileSponsors).
//
// Each KeyVisual is scaled so the whole composition stays visible (like
// `object-fit: contain`), centred on the page. Canvases are transparent over
// the page's grain background, so the leftover space reads as margin. To use a bit more of the
// screen, it may grow past `contain` until up to half of the canvas's outer
// margin (between the canvas edge and the frame/text) is cropped — never
// beyond `cover`. See `fitCanvas`.
//
// Everything inside is positioned as a percentage of the container's own
// width (cqw) or height (%), so it scales proportionally with the canvas.
//
// The frame border and the CTA badge/play button are drawn as SVG shapes
// inside FrameSvg (custom per-corner rounding that CSS border-radius can't
// express) — the CtaText/PlayButton components here are just the text and
// the transparent click target layered on top of them.

const DesktopMedia = '(min-aspect-ratio: 146/100)';
const LaptopMedia = '(min-aspect-ratio: 95/100) and (max-aspect-ratio: 146/100)';
const TabletMedia = '(min-aspect-ratio: 68/100) and (max-aspect-ratio: 95/100)';
const MobileMedia = '(max-aspect-ratio: 68/100)';

const round = (value: number) => Math.round(value * 10000) / 10000;

// Space the fixed accessibility widget (bottom-right, 20px inset, 50px wide)
// takes from each edge, plus a small gap.
const WidgetClearance = 78;

const VideoModalWidth = '90vw';

// Vertical space the modal takes besides the video (dialog margins, body
// padding, close button and the versions row below the video). The video's
// max height is the viewport minus this, so the modal never overflows it, and
// the dialog is narrowed so a 16:9 video at that height still fills its width.
const VideoModalChrome = 260;
const VideoModalChromeMobile = 300;

const VersionDefaultColor = '#333333';
const VersionActiveColor = '#003367';
const VersionButtonSize = 76;
const VersionButtonSizeMobile = 72;

// Width of a canvas of `width`x`height` whose safe area (the part that must
// stay visible) is `safeWidth`x`safeHeight`, centred in the canvas:
// the largest size at which the safe area still fits the viewport, capped at
// the `cover` size. Height follows from aspect-ratio. `svh` (viewport height
// with the mobile browser toolbars shown) comes after a `vh` fallback.
//
// `corner` is the bottom-right corner of the sponsor strip, as fractions of
// the canvas. When given, the canvas is also kept small enough that the strip
// clears the accessibility widget either horizontally or vertically
// (whichever costs less size).
const fitCanvas = (width: number, height: number, safeWidth: number, safeHeight: number, corner?: { right: number; bottom: number }) => {
    const cover = (unit: string) => `max(100vw, ${round((width / height) * 100)}${unit})`;
    const safe = (unit: string) => `min(${round((width / safeWidth) * 100)}vw, ${round((width / safeHeight) * 100)}${unit})`;
    const clear = (unit: string) =>
        corner
            ? `, max(
                calc((100vw - ${2 * WidgetClearance}px) / ${round(2 * corner.right - 1)}),
                calc((100${unit} - ${2 * WidgetClearance}px) * ${round(width / height / (2 * corner.bottom - 1))})
            )`
            : '';

    return css`
        flex-shrink: 0;
        aspect-ratio: ${width} / ${height};
        width: min(${cover('vh')}, ${safe('vh')}${clear('vh')});
        width: min(${cover('svh')}, ${safe('svh')}${clear('svh')});
    `;
};

const keyVisual = (media: string) => css`
    display: none;
    position: relative;
    overflow: hidden;
    font-family: 'Big Shoulders Display', sans-serif;
    -webkit-font-smoothing: antialiased;
    container-type: inline-size;

    @media only screen and ${media} {
        display: block;
    }
`;

const StyledSennaBeco = {
    VideoModal: styled(Modal)`
        .modal-dialog {
            width: min(${VideoModalWidth}, calc((100vh - ${VideoModalChrome}px) * 16 / 9 + 48px));
            width: min(${VideoModalWidth}, calc((100svh - ${VideoModalChrome}px) * 16 / 9 + 48px));
            max-width: none;
            margin: 1.75rem auto;
        }

        .modal-content {
            overflow: visible;
            border: 0;
            border-radius: 10px;
            background-color: #ffffff;
            box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
        }

        @media only screen and (max-width: 768px) {
            .modal-dialog {
                width: min(${VideoModalWidth}, calc(100% - 24px));
                max-width: min(${VideoModalWidth}, calc(100% - 24px));
            }
        }
    `,

    VideoModalBody: styled(Modal.Body)`
        position: relative;
        padding: 24px;

        @media only screen and (max-width: 768px) {
            padding: 16px;
        }
    `,

    VideoModalCloseContainer: styled.div`
        height: 25px;
        margin-bottom: 16px;
        text-align: right;

        @media only screen and (max-width: 768px) {
            margin-bottom: 12px;
        }
    `,

    VideoModalClose: styled.img`
        width: auto;
        height: 25px;
        margin-right: 15px;
        transform: translateX(20px);
        cursor: pointer;
    `,

    VideoPlayer: styled.video`
        display: block;
        width: 100%;
        height: auto;
        max-height: calc(100vh - ${VideoModalChrome}px);
        max-height: calc(100svh - ${VideoModalChrome}px);
        margin: 0 auto;
        background-color: #000000;
        box-shadow: rgba(149, 157, 165, 0.5) 0px 8px 24px;

        @media only screen and (max-width: 768px) {
            max-height: calc(100vh - ${VideoModalChromeMobile}px);
            max-height: calc(100svh - ${VideoModalChromeMobile}px);
            box-shadow: none;
        }
    `,

    // Desktop: buttons centred in the modal with the label hugging their left
    // side (the empty third column balances the label so the buttons stay centred).
    VideoVersions: styled.div`
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        gap: 20px;
        margin-top: 20px;

        @media only screen and (max-width: 768px) {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 16px;
        }
    `,

    VideoVersionsLabel: styled.p`
        margin: 0;
        font-family: 'Barlow', sans-serif;
        font-size: 16px;
        font-weight: 700;
        color: ${VersionActiveColor};
        text-align: left;
        justify-self: end;

        @media only screen and (max-width: 768px) {
            font-size: 14px;
            text-align: center;
        }
    `,

    VideoVersionsButtons: styled.div`
        display: flex;
        gap: 20px;

        @media only screen and (max-width: 768px) {
            gap: 16px;
        }
    `,

    VideoVersionButton: styled.button<{ $active: boolean }>`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        width: ${VersionButtonSize}px;
        height: ${VersionButtonSize}px;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background-color: ${({ $active }) => ($active ? VersionActiveColor : VersionDefaultColor)};
        color: #ffffff;
        font-family: 'Barlow', sans-serif;
        font-size: 8px;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;
        box-shadow: ${({ $active }) => ($active ? `0 0 0 3px #ffffff, 0 0 0 6px ${VersionActiveColor}, ` : '')}rgba(0, 0, 0, 0.25) 0px 4px 8px;
        transition: transform 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

        img {
            width: auto;
            height: 30px;
        }

        &:hover {
            transform: scale(1.05);
        }

        &:focus {
            outline: none;
        }

        &:focus-visible {
            outline: 3px solid ${VersionActiveColor};
            outline-offset: 8px;
        }

        @media only screen and (max-width: 768px) {
            width: ${VersionButtonSizeMobile}px;
            height: ${VersionButtonSizeMobile}px;
            font-size: 7px;
            letter-spacing: -0.1px;

            img {
                height: 26px;
            }
        }
    `,

    // Centres the visible key visual in the viewport and crops whatever the
    // safe-area sizing lets overflow. On mobile the page grows past the
    // viewport instead, so the sponsor list below the key visual can scroll.
    Page: styled.div<{ $grainSrc: string }>`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        position: relative;
        width: 100%;
        height: 100vh;
        height: 100svh;
        overflow: hidden;
        background-color: #eae8e7;
        // One grain layer behind every canvas (they're transparent), so the
        // texture doesn't change scale at the canvas edge. 12.66vw matches the
        // designs' grain scale when the canvas spans the viewport width.
        background-image: url(${props => props.$grainSrc});
        background-size: 12.66vw 12.66vw;

        @media only screen and ${MobileMedia} {
            justify-content: flex-start;
            height: auto;
            min-height: 100vh;
            min-height: 100svh;
            overflow-x: hidden;
            overflow-y: visible;
        }
    `,

    FrameSvg: styled.svg`
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
    `,

    // ----- Desktop key visual (1920x1080 canvas) -----

    KeyVisual: styled.div`
        ${keyVisual(DesktopMedia)}
        // Frame spans x 73-1857, content spans y 54-1042.
        ${fitCanvas(1920, 1080, 1857, 1042, { right: 0.968, bottom: 0.95 })}
    `,

    // Aligned with the frame's left edge (x 73).
    Eyebrow: styled.div`
        position: absolute;
        left: 3.8021%;
        top: 5%;
        font-size: 1.276cqw;
        font-weight: 700;
        letter-spacing: 0.005em;
        color: #4f4f4f;
    `,

    EyebrowHighlight: styled.span`
        color: #9d7644;
    `,

    Headline: styled.div`
        position: absolute;
        left: 9.0625%;
        top: 27.5926%;
        display: flex;
        flex-direction: column;
    `,

    HeadlineLine: styled.div`
        font-size: 3.9271cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        line-height: 1;
        color: #6b6a68;

        & + & {
            margin-top: 1.4219cqw;
        }
    `,

    HeadlineHighlight: styled.div`
        font-size: 8.125cqw;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
        color: #9d7644;
        margin-top: 0.4063cqw;
    `,

    CollageMask: styled.div`
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 84.3519%;
        overflow: hidden;
    `,

    Collage: styled.img`
        position: absolute;
        left: 27.6042%;
        top: -12.7333%;
        width: 44.5312%;
        height: 121.9539%;
    `,

    Description: styled.div`
        position: absolute;
        left: 8.5938%;
        top: 88.7037%;
        font-family: 'Barlow', sans-serif;
        font-size: 1.5104cqw;
        font-weight: 400;
        line-height: 1.45;
        color: #6b6a68;
    `,

    CtaText: styled.div`
        position: absolute;
        left: 77.0052%;
        top: 14.9722%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `,

    CtaTextLine: styled.div`
        font-size: 2.2396cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #ffffff;
        line-height: 1;
        white-space: nowrap;

        & + & {
            margin-top: 0.5938cqw;
        }
    `,

    // Transparent hit area over the play button drawn in FrameSvg.
    PlayButton: styled.a`
        position: absolute;
        left: 85.9375%;
        top: 30.5556%;
        width: 10.7812%;
        height: 15.7407%;
        cursor: pointer;
    `,

    // Classificação indicativa, inside the frame's bottom-right corner.
    ClassificationBadge: styled.img`
        position: absolute;
        left: 90.7292%;
        top: 71.7593%;
        width: 3.5937cqw;
        height: auto;
    `,

    FooterContainer: styled.div`
        position: absolute;
        left: 51.9271%;
        top: 88.3333%;
        width: 44.7917%;
        font-family: 'Barlow', sans-serif;
    `,

    FooterLabelPatrocinio: styled.div`
        position: absolute;
        left: 27.0248%;
        top: 0;
        font-size: 0.6771cqw;
        font-weight: 700;
        color: #111111;
    `,

    FooterLabelRealizacao: styled.div`
        position: absolute;
        left: 45.2907%;
        top: 0;
        font-size: 0.6771cqw;
        font-weight: 700;
        color: #111111;
    `,

    FooterLogosRow: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.9375cqw;
        height: 2.6042cqw;
        margin-top: 1.5625cqw;
    `,

    FooterLogoRouanet: styled.img`
        width: 6.3021cqw;
        height: auto;
    `,

    FooterLogoItau: styled.img`
        height: 2.0313cqw;
        width: auto;
    `,

    FooterLogoAlvarezMarsal: styled.img`
        height: 2.0313cqw;
        width: auto;
    `,

    FooterLogoShp: styled.img`
        height: 2.0833cqw;
        width: auto;
    `,

    FooterLogoSenna: styled.img`
        width: 3.9583cqw;
        height: auto;
    `,

    FooterLogoSeparator: styled.img`
        width: 1px;
        height: 1.9271cqw;
    `,

    FooterLogoInstituto: styled.img`
        width: 3.3333cqw;
        height: auto;
    `,

    FooterLogoMinc: styled.img`
        width: 6.9271cqw;
        height: auto;
    `,

    // ----- Laptop key visual (1225x1018 canvas) -----

    KeyVisualLaptop: styled.div`
        ${keyVisual(LaptopMedia)}
        // Frame spans x 40-1185, content spans y 80-936.
        ${fitCanvas(1225, 1018, 1185, 938, { right: 0.969, bottom: 0.903 })}
    `,

    // Aligned with the frame's left edge (x 40).
    LaptopEyebrow: styled.div`
        position: absolute;
        left: 3.2653%;
        top: 7.8585%;
        font-size: 1.7959cqw;
        font-weight: 700;
        letter-spacing: 0.005em;
        line-height: 1;
        color: #4f4f4f;
    `,

    // The laptop design positions each headline line independently.
    LaptopHeadlineLine: styled.div<{ $top: string }>`
        position: absolute;
        left: 7.3469%;
        top: ${props => props.$top};
        font-size: 4.1306cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        line-height: 1;
        color: #6b6a68;
    `,

    LaptopHeadlineHighlight: styled.div<{ $top: string }>`
        position: absolute;
        left: 7.3469%;
        top: ${props => props.$top};
        font-size: 8.6367cqw;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
        color: #9d7644;
    `,

    // Clips the collage at the frame's bottom edge.
    LaptopCollageMask: styled.div`
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 81.6306%;
        overflow: hidden;
    `,

    LaptopCollage: styled.img`
        position: absolute;
        left: 26.7755%;
        top: 6.8592%;
        width: 52.2449%;
        height: 100.1203%;
    `,

    LaptopDescription: styled.div`
        position: absolute;
        left: 7.3469%;
        top: 85.6582%;
        font-family: 'Barlow', sans-serif;
        font-size: 1.7959cqw;
        font-weight: 400;
        line-height: 1.45;
        color: #6b6a68;
    `,

    LaptopCtaText: styled.div`
        position: absolute;
        left: 76.4898%;
        top: 17.3477%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `,

    LaptopCtaTextLine: styled.div`
        font-size: 2.3673cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #ffffff;
        line-height: 1;
        white-space: nowrap;

        & + & {
            margin-top: 0.7143cqw;
        }
    `,

    // Transparent hit area over the play button drawn in FrameSvg.
    LaptopPlayButton: styled.a`
        position: absolute;
        left: 85.3061%;
        top: 28.6837%;
        width: 11.4286%;
        height: 10.8055%;
        cursor: pointer;
    `,

    // Classificação indicativa, inside the frame's bottom-right corner.
    LaptopClassificationBadge: styled.img`
        position: absolute;
        left: 90.2041%;
        top: 72.2986%;
        width: 3.9184cqw;
        height: auto;
    `,

    LaptopFooterContainer: styled.div`
        position: absolute;
        left: 40.5714%;
        top: 84.6758%;
        width: 56.3265%;
        font-family: 'Barlow', sans-serif;
    `,

    LaptopFooterLabelPatrocinio: styled.div`
        position: absolute;
        left: 27.0623%;
        top: 0;
        font-size: 0.898cqw;
        font-weight: 700;
        color: #111111;
    `,

    LaptopFooterLabelRealizacao: styled.div`
        position: absolute;
        left: 45.3333%;
        top: 0;
        font-size: 0.898cqw;
        font-weight: 700;
        color: #111111;
    `,

    LaptopFooterLogosRow: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 1.1755cqw;
        height: 3.2653cqw;
        margin-top: 1.9592cqw;
    `,

    LaptopFooterLogoRouanet: styled.img`
        width: 7.902cqw;
        height: auto;
    `,

    LaptopFooterLogoItau: styled.img`
        height: 2.5469cqw;
        width: auto;
    `,

    LaptopFooterLogoAlvarezMarsal: styled.img`
        height: 2.5469cqw;
        width: auto;
    `,

    LaptopFooterLogoShp: styled.img`
        height: 2.6122cqw;
        width: auto;
    `,

    LaptopFooterLogoSenna: styled.img`
        width: 4.9633cqw;
        height: auto;
    `,

    LaptopFooterLogoSeparator: styled.img`
        width: 1px;
        height: 2.4163cqw;
    `,

    LaptopFooterLogoInstituto: styled.img`
        width: 4.1796cqw;
        height: auto;
    `,

    LaptopFooterLogoMinc: styled.img`
        width: 8.6857cqw;
        height: auto;
    `,

    // ----- Tablet key visual (1024x1366 canvas) -----

    KeyVisualTablet: styled.div`
        ${keyVisual(TabletMedia)}
        // Frame spans x 59-965, content spans y 44-1268.
        ${fitCanvas(1024, 1366, 965, 1322, { right: 0.908, bottom: 0.923 })}
    `,

    // Aligned with the frame's left edge (x 59).
    TabletEyebrow: styled.div`
        position: absolute;
        left: 5.7617%;
        top: 3.2211%;
        font-size: 1.9531cqw;
        font-weight: 700;
        letter-spacing: 0.005em;
        color: #4f4f4f;
    `,

    TabletHeadline: styled.div`
        position: absolute;
        left: 11.2305%;
        top: 12.7818%;
        display: flex;
        flex-direction: column;
    `,

    TabletHeadlineLine: styled.div`
        font-size: 4.7852cqw;
        font-weight: 700;
        letter-spacing: 0.01em;
        line-height: 1;
        color: #6b6a68;

        & + & {
            margin-top: 1.6504cqw;
        }
    `,

    TabletHeadlineHighlight: styled.div`
        font-size: 10.0586cqw;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
        color: #9d7644;
        margin-top: 0.6055cqw;
    `,

    TabletCollage: styled.img`
        position: absolute;
        left: 15.8203%;
        top: 17.2767%;
        width: 66.6016%;
        height: 60.9078%;
    `,

    TabletDescription: styled.div`
        position: absolute;
        left: 28.5156%;
        top: 77.1596%;
        font-family: 'Barlow', sans-serif;
        font-size: 2.5391cqw;
        font-weight: 400;
        line-height: 1.45;
        color: #6b6a68;
    `,

    TabletCtaText: styled.div`
        position: absolute;
        left: 63.6719%;
        top: 8.6384%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `,

    TabletCtaTextLine: styled.div`
        font-size: 3.5156cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #ffffff;
        line-height: 1;
        white-space: nowrap;

        & + & {
            margin-top: 0.9082cqw;
        }
    `,

    // Transparent hit area over the play button drawn in FrameSvg.
    TabletPlayButton: styled.a`
        position: absolute;
        left: 76.1719%;
        top: 19.0337%;
        width: 18.0664%;
        height: 11.6398%;
        cursor: pointer;
    `,

    // Classificação indicativa, inside the frame's bottom-right corner (right of the collage).
    TabletClassificationBadge: styled.img`
        position: absolute;
        left: 84.668%;
        top: 66.7643%;
        width: 5.6641cqw;
        height: auto;
    `,

    TabletFooterContainer: styled.div`
        position: absolute;
        left: 12.6953%;
        top: 87.4085%;
        width: 74.6094%;
        font-family: 'Barlow', sans-serif;
    `,

    TabletFooterLabelPatrocinio: styled.div`
        position: absolute;
        left: 20.3931%;
        top: 0;
        font-size: 1.1719cqw;
        font-weight: 700;
        color: #111111;
    `,

    TabletFooterLabelRealizacao: styled.div`
        position: absolute;
        left: 39.4318%;
        top: 0;
        font-size: 1.1719cqw;
        font-weight: 700;
        color: #111111;
    `,

    TabletFooterLogosRow: styled.div`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1.626cqw;
        height: 4.4922cqw;
        margin-top: 2.7344cqw;
    `,

    TabletFooterLogoRouanet: styled.img`
        width: 10.9302cqw;
        height: auto;
    `,

    TabletFooterLogoItau: styled.img`
        height: 3.5229cqw;
        width: auto;
    `,

    TabletFooterLogoAlvarezMarsal: styled.img`
        height: 3.5229cqw;
        width: auto;
    `,

    TabletFooterLogoShp: styled.img`
        height: 3.6133cqw;
        width: auto;
    `,

    TabletFooterLogoSenna: styled.img`
        width: 6.8652cqw;
        height: auto;
    `,

    TabletFooterLogoSeparator: styled.img`
        width: 1px;
        height: 3.3423cqw;
    `,

    TabletFooterLogoInstituto: styled.img`
        width: 5.7813cqw;
        height: auto;
    `,

    TabletFooterLogoMinc: styled.img`
        width: 12.0142cqw;
        height: auto;
    `,

    // ----- Mobile key visual (1080x1740 canvas) -----

    KeyVisualMobile: styled.div`
        ${keyVisual(MobileMedia)}
        // Frame spans x 55-1026, content spans y 58-1676.
        ${fitCanvas(1080, 1740, 1026, 1682)}
    `,

    // Aligned with the frame's left edge (x 55).
    MobileEyebrow: styled.div`
        position: absolute;
        left: 5.0926%;
        top: 3.3333%;
        font-size: 2.2685cqw;
        font-weight: 700;
        letter-spacing: 0.005em;
        color: #4f4f4f;
    `,

    MobileHeadline: styled.div`
        position: absolute;
        left: 11.0185%;
        top: 14.2529%;
        display: flex;
        flex-direction: column;
    `,

    MobileHeadlineLine: styled.div`
        font-size: 6.5cqw;
        font-weight: 700;
        letter-spacing: 0.01em;
        line-height: 1;
        color: #6b6a68;

        & + & {
            margin-top: 2.3148cqw;
        }
    `,

    MobileHeadlineHighlight: styled.div`
        font-size: 13.6111cqw;
        font-weight: 700;
        letter-spacing: 0;
        line-height: 1;
        color: #9d7644;
        margin-top: 0.7407cqw;
    `,

    MobileCollage: styled.img`
        position: absolute;
        left: 15.3704%;
        top: 34.8851%;
        width: 71.2963%;
        height: 57.4712%;
    `,

    MobileCtaText: styled.div`
        position: absolute;
        left: 64.537%;
        top: 9.1954%;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    `,

    MobileCtaTextLine: styled.div`
        font-size: 3.3333cqw;
        font-weight: 700;
        letter-spacing: 0.02em;
        color: #ffffff;
        line-height: 1;
        white-space: nowrap;

        & + & {
            margin-top: 1.2593cqw;
        }
    `,

    // Transparent hit area over the play button drawn in FrameSvg.
    MobilePlayButton: styled.a`
        position: absolute;
        left: 78.2407%;
        top: 17.5862%;
        width: 16.7593%;
        height: 8.6207%;
        cursor: pointer;
    `,

    MobileDescription: styled.div`
        position: absolute;
        left: 0;
        right: 0;
        top: 91.4943%;
        text-align: center;
        font-family: 'Barlow', sans-serif;
        font-size: 2.6852cqw;
        font-weight: 400;
        line-height: 1.45;
        color: #6b6a68;
    `,

    // Classificação indicativa, inside the frame's bottom-right corner (right of the collage).
    MobileClassificationBadge: styled.img`
        position: absolute;
        left: 86.7593%;
        top: 83.046%;
        width: 5.463cqw;
        height: auto;
    `,

    // Vertical sponsor list below the mobile key visual. Logo sizes are the
    // web sponsor strip's (laptop design, 1225px canvas), so the logos keep
    // the same proportions to each other.
    MobileSponsors: styled.div`
        display: none;
        flex-direction: column;
        width: 160px;
        margin: 8px auto 0;
        // Room for the fixed accessibility widget in the bottom-right corner.
        padding-bottom: 96px;

        @media only screen and ${MobileMedia} {
            display: flex;
        }
    `,

    MobileSponsorLogo: styled.div`
        display: flex;
        align-items: center;
        justify-content: center;
        height: 72px;

        & + & {
            border-top: 1px solid #6b6a68;
        }
    `,

    MobileSponsorLogoRouanet: styled.img`
        width: 117px;
        height: auto;
    `,

    MobileSponsorLogoItau: styled.img`
        height: 38px;
        width: auto;
    `,

    MobileSponsorLogoAlvarezMarsal: styled.img`
        height: 38px;
        width: auto;
    `,

    MobileSponsorLogoShp: styled.img`
        height: 38px;
        width: auto;
    `,

    MobileSponsorLogoSenna: styled.img`
        height: 21px;
        width: auto;
    `,

    MobileSponsorLogoInstituto: styled.img`
        height: 35px;
        width: auto;
    `,

    MobileSponsorLogoMinc: styled.img`
        width: 128px;
        height: auto;
    `,
};

export default StyledSennaBeco;
